import React from 'react';
import { Button, Col, ControlLabel, HelpBlock, Row } from 'react-bootstrap';
import { Field } from 'react-final-form';
import {
  DragDropContext,
  Draggable,
  Droppable,
  resetServerContext,
} from 'react-beautiful-dnd';
import CheckboxAdapter from '../../CheckboxAdapter.jsx';
import FieldGroup from '../../FieldGroup.jsx';
import { validateEditorData } from '../../editor/defaultOptions.js';
import { FormError } from '../../FormError.jsx';
import { ProjectStakeholdersLink } from '../../links/index.jsx';

import Editor from '../../editor/editor.jsx';

const getListStyle = (isDraggingOver) => ({
  background: isDraggingOver ? 'none' : 'none',
});

const StakeholdersSettingsFrom = (props) => {
  const {
    submitError,
    submitFailed,
    submitting,
    errors,
    handleSubmit,
    onDragEnd,
    onCreateColumn,
    onDeleteColumn,
    onColumnNameChange,
    onContentTypeChange,
    onTableShowChange,
    setCommentEditorInstance,
    data,
    values,
    projectId,
  } = props;

  resetServerContext();

  const indexOfInfluence = () =>
    data.findIndex((el) => el.nativeColumn === 'influence');
  const indexOfInterest = () =>
    data.findIndex((el) => el.nativeColumn === 'interest');

  return (
    <form onSubmit={handleSubmit}>
      <Row>
        <Col xs={12}>
          <div className="component-sm component-bg-blue component-hide-border component-margin-bottom">
            <h3>Stakeholder Analysis Table</h3>
          </div>
        </Col>
      </Row>

      <Row>
        <Col xs={8}>
          <p className="communication-help text-muted m-b-24">
            You can easily add, edit, rename, and delete stakeholder analysis
            titles. Also, you can reorder columns by dragging them.
          </p>
        </Col>
      </Row>

      <Row className="m-b-60">
        <Col xs={12}>
          <DragDropContext onDragEnd={(result) => onDragEnd(result)}>
            <Droppable droppableId="droppable">
              {(provided, snapshot) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  style={getListStyle(snapshot.isDraggingOver)}
                >
                  {data.map((item, index) => (
                    <Draggable
                      key={item.draggableId}
                      draggableId={item.draggableId}
                      index={index}
                    >
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                        >
                          <Row>
                            <Col xs={1}>
                              <span
                                className="btn-move btn btn-default"
                                {...provided.dragHandleProps}
                              ></span>
                            </Col>
                            <Col xs={4}>
                              <Field
                                id={item.draggableId}
                                name={item.draggableId}
                                className="m-0"
                                placeholder="Enter Title"
                                disabled={
                                  item.nativeColumn === 'influence' ||
                                  item.nativeColumn === 'interest' ||
                                  item.nativeColumn === 'support' ||
                                  item.nativeColumn === 'engagement_frequency'
                                }
                                type="text"
                                label=""
                              >
                                {(props) => (
                                  <FieldGroup
                                    {...props}
                                    value={item.name}
                                    onChange={(event) =>
                                      onColumnNameChange(
                                        event.target.value,
                                        index
                                      )
                                    }
                                  />
                                )}
                              </Field>
                            </Col>
                            <Col xs={1}>
                              <button
                                type="button"
                                disabled={!item.allowDelete}
                                className="btn-delete btn btn-default"
                                onClick={() => onDeleteColumn(item.draggableId)}
                              ></button>
                            </Col>
                            <Col xs={3}>
                              <Field
                                name={`${item.draggableId}TableShow`}
                                label="Show in Table"
                                type="checkbox"
                                component={CheckboxAdapter}
                                checked={item.tableShow}
                                onClick={() => onTableShowChange(index)}
                              />
                            </Col>
                            <Col xs={3}>
                              <Field
                                name={`${item.draggableId}IsEditor`}
                                label="Rich Text Editor Field"
                                type="checkbox"
                                component={CheckboxAdapter}
                                checked={item.editor}
                                disabled={!item.allowEditor}
                                onClick={() => onContentTypeChange(index)}
                              />
                            </Col>
                          </Row>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </Col>
        <Col xs={12}>
          <button
            disabled={!data[data.length - 1].name}
            className="btn-link"
            onClick={(event) => {
              event.preventDefault();
              onCreateColumn();
            }}
          >
            {' '}
            + Create New Column
          </button>
        </Col>
      </Row>

      <Row>
        <Col xs={12}>
          <div className="component-sm component-bg-blue component-hide-border component-margin-bottom">
            <h3>Stakeholder Map</h3>
          </div>
        </Col>
      </Row>

      <Row>
        <Col xs={8}>
          <p className="communication-help text-muted m-b-24">
            Rename the axis and quadrants of the map. Changes will automatically
            appear in the Stakeholder Analysis table as well
          </p>
        </Col>
      </Row>

      <Row>
        <Col xs={8}>
          <FieldGroup
            id="xAxis"
            label="X axis"
            type="text"
            meta={{ error: errors?.xAxis, touched: true }}
            input={{
              name: 'xAxis',
              value: data[indexOfInterest()].name,
              onChange: (event) =>
                onColumnNameChange(event.target.value, indexOfInterest()),
            }}
          />
        </Col>
      </Row>

      <Row>
        <Col xs={8}>
          <FieldGroup
            id="yAxis"
            label="Y axis"
            type="text"
            meta={{ error: errors?.yAxis, touched: true }}
            input={{
              name: 'yAxis',
              value: data[indexOfInfluence()].name,
              onChange: (event) =>
                onColumnNameChange(event.target.value, indexOfInfluence()),
            }}
          />
        </Col>
      </Row>

      <Row>
        <Col xs={8}>
          <Field
            id="topLeftQuadrantLabel"
            name="topLeftQuadrantLabel"
            label="Top Left Quadrant Label"
            type="text"
            component={FieldGroup}
          />
        </Col>
      </Row>

      <Row>
        <Col xs={8}>
          <Field
            id="topRightQuadrantLabel"
            name="topRightQuadrantLabel"
            label="Top Right Quadrant Label"
            type="text"
            component={FieldGroup}
          />
        </Col>
      </Row>

      <Row>
        <Col xs={8}>
          <Field
            id="bottomLeftQuadrantLabel"
            name="bottomLeftQuadrantLabel"
            label="Bottom Left Quadrant Label"
            type="text"
            component={FieldGroup}
          />
        </Col>
      </Row>

      <Row className="m-b-24">
        <Col xs={8}>
          <Field
            id="bottomRightQuadrantLabel"
            name="bottomRightQuadrantLabel"
            label="Bottom Right Quadrant Label"
            type="text"
            component={FieldGroup}
          />
        </Col>
      </Row>

      <Row className="m-b-16">
        <Col xs={8}>
          <ControlLabel>Comment</ControlLabel>
          <Field
            name="addVersionComment"
            label="Add version comment"
            type="checkbox"
            component={CheckboxAdapter}
          />
        </Col>
      </Row>

      <Row style={{ display: !values.addVersionComment ? 'none' : '' }}>
        <Col xs={8}>
          <p className="communication-help text-muted m-b-24">
            Add an optional comment about the changes in Stakeholder Analysis.
          </p>
          <Field name="changesComment">
            {({ name, input }) => {
              return (
                <Editor
                  name={name || ''}
                  data={validateEditorData(input.value)}
                  label=""
                  setEditorInstanceForParent={setCommentEditorInstance}
                  options={{
                    holder: 'comment-stakeholder-editor',
                  }}
                />
              );
            }}
          </Field>
        </Col>
      </Row>

      <Row className="m-b-48">
        {submitFailed ? (
          <HelpBlock>
            <FormError
              submitError={submitError || 'Please check all required fields'}
            />
          </HelpBlock>
        ) : null}
        <Col xs={12}>
          <div className="flex">
            <div>
              <ProjectStakeholdersLink projectId={projectId}>
                <Button bsStyle="default">Cancel</Button>
              </ProjectStakeholdersLink>
              <Button
                className="m-l-12"
                bsStyle="primary"
                disabled={submitting}
                type="submit"
              >
                {submitting ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </Col>
      </Row>
    </form>
  );
};

export default StakeholdersSettingsFrom;
